import type { Platform } from '@hangar/shared';
import { cn } from '@/lib/utils';
import { FormField, fieldA11y } from '@/components/FormField';
import { PlatformIcon } from '@/components/badges';
import { Label } from '@/components/ui/label';
import { OptionSelect } from '@/components/OptionSelect';
import { useSeesProviders } from '@/lib/useAudience';
import { accountOptions, accountPlaceholder } from './profileFormModel';
import type { ProfileFormApi } from './useProfileForm';

const PLATFORMS: Platform[] = ['android', 'ios'];

/** Android / iOS radio toggle; fixed while editing. */
function PlatformToggle({ form }: { form: ProfileFormApi }) {
  const { values, edit, setPlatform } = form;
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-[13px]">Platform</Label>
      <div role="radiogroup" aria-label="Platform" className="grid grid-cols-2 gap-1 rounded-lg border bg-secondary p-1">
        {PLATFORMS.map((pl) => (
          <button
            key={pl}
            type="button"
            role="radio"
            aria-checked={values.platform === pl}
            disabled={edit}
            data-testid={`profile-platform-${pl}`}
            onClick={() => setPlatform(pl)}
            className={cn('flex h-9 items-center justify-center gap-1.5 rounded-md text-[13px] font-medium transition-colors duration-150 max-sm:h-11', values.platform === pl ? 'bg-card text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground', edit && 'cursor-not-allowed opacity-70')}
          >
            <PlatformIcon platform={pl} size={16} />
            {pl === 'android' ? 'Android' : 'iOS'}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * The build service account select: picking one resets the execution modes to that kind's default. Platform operators
 * read the provider accounts; clients read neutral names ("Hangar build service"), SPEC decision 23.
 */
function AccountField({ form }: { form: ProfileFormApi }) {
  const { values, selectable, errorFor, pickAccount } = form;
  const sees = useSeesProviders();
  return (
    <FormField id="profile-account" label={sees ? 'Provider account' : 'Build service'} required error={errorFor('providerAccountId')}>
      <OptionSelect
        data-testid="profile-account"
        value={values.providerAccountId}
        onValueChange={pickAccount}
        placeholder={accountPlaceholder(selectable.length, sees)}
        options={accountOptions(selectable, sees)}
        {...fieldA11y('profile-account', errorFor('providerAccountId'))}
      />
    </FormField>
  );
}

export function ProfilePlatformAccount({ form }: { form: ProfileFormApi }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <PlatformToggle form={form} />
      <AccountField form={form} />
    </div>
  );
}
